import React, { useContext, useEffect, useState, useCallback } from 'react';

import { AngularContext, AngularServicesContext } from 'react-app';
import t from 'react-translate';
import { Button } from 'react-bootstrap';
import { useAppDispatch } from 'redux/store';
import { configureWorkday } from 'redux/actions/institutions';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';

import NvModal, { ModalType } from 'shared/components/nv-modal';
import ManageTopics from './workday/manage-topics';
import { DisableProps } from './integrations';
import SectionContent from '../section-content';
import { config } from '../../../../../config/pendo.config.json';

const providerKeys = {
  saba: 'SABA',
  sum_total: 'SUMTOTAL',
  csod: 'CSOD',
  learn_upon: 'LEARNUPON',
  workday_learn: 'WORKDAY_LEARN',
};
export const providerTypes = {
  SABA: 'saba',
  SUMTOTAL: 'sum_total',
  CSOD: 'csod',
  LEARNUPON: 'learn_upon',
  WORKDAY_LEARN: 'workday_learn',
};

const LmsIntegration = (props: { onDisable: (props: DisableProps) => void }) => {
  const { $uibModal } = useContext(AngularServicesContext);
  const { injectServices, $scope } = useContext(AngularContext);
  const [InstitutionsManager] = injectServices(['InstitutionsManager']);
  const [lmsEnabled, setLmsEnabled] = useState($scope.$eval('vm.InstitutionsManager.institution.lmsEnabled'));
  const [lmsConnectionSettings, setLmsConnectionSettings] = useState($scope.$eval('vm.InstitutionsManager.institution.lmsConnectionSettings'));
  const [isWorkdayEnabled, setIsWorkdayEnabled] = useState($scope.$eval('vm.InstitutionsManager.institution.isWorkdayLearnEnabled'));
  const [workdayConfig, setWorkdayConfig] = useState($scope.$eval('vm.InstitutionsManager.institution.workdayLearnConfig'));
  const [showManageTopicsModal, setShowManageTopicsModal] = useState(false);
  const [isTopicsFormDirty, setIsTopicsFormDirty] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    $scope.$watch('vm.InstitutionsManager.institution.lmsEnabled', (newValue) => {
      setLmsEnabled(newValue);
    });
  }, [$scope]);

  useEffect(() => {
    $scope.$watch('vm.InstitutionsManager.institution.lmsConnectionSettings', (newValue) => {
      setLmsConnectionSettings(newValue);
    });
  }, [$scope]);

  useEffect(() => {
    $scope.$watch('vm.InstitutionsManager.institution.isWorkdayLearnEnabled', (newValue) => {
      setIsWorkdayEnabled(newValue);
      if (!newValue) return;
      if (lmsConnectionSettings?.lms === providerTypes.WORKDAY_LEARN) return;
      setLmsConnectionSettings({ lms: providerTypes.WORKDAY_LEARN });
    });
  }, [$scope]);

  useEffect(() => {
    $scope.$watch('vm.InstitutionsManager.institution.workdayLearnConfig', (newValue) => {
      setWorkdayConfig(newValue);
    });
  }, [$scope]);

  const showConfigureBtn = () => {
    const noSabaConnections = lmsConnectionSettings && !lmsConnectionSettings.sabaDomainName;
    const sumTotalConnections = lmsConnectionSettings?.lms === providerTypes.SUMTOTAL;
    const csodConnections = lmsConnectionSettings?.lms === providerTypes.CSOD;
    const learnUponConnections = lmsConnectionSettings?.lms === providerTypes.LEARNUPON;
    const workdayLearnConnections = isWorkdayEnabled && !!workdayConfig;

    return (!lmsConnectionSettings
      || (noSabaConnections
        && !sumTotalConnections
        && !csodConnections
        && !learnUponConnections
        && !workdayLearnConnections
      )
    );
  };

  const getProviderName = () => {
    const providerType = lmsConnectionSettings
      ? lmsConnectionSettings.lms : '';
    return providerType === providerTypes.SABA
      ? lmsConnectionSettings.lms.toUpperCase()
      : t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.MODAL.PROVIDER_TYPE[providerKeys[providerType]]();
  };

  const showSumTotalSection = () => lmsConnectionSettings?.lms === providerTypes.SUMTOTAL;

  const showCornerstoneSection = () => lmsConnectionSettings?.lms === providerTypes.CSOD;

  const showlearnUponSection = () => lmsConnectionSettings?.lms === providerTypes.LEARNUPON;

  const showWorkdayLearnSection = () => isWorkdayEnabled && workdayConfig;

  const configureLms = () => {
    $uibModal.open({
      templateUrl: 'institutions/templates/configure-lms-modal.html',
      controller: 'ConfigureLmsModalCtrl as vm',
    });
  };

  const disableLms = () => {
    setIsDisabling(true);
    props.onDisable({
      action: () => InstitutionsManager.disableLmsIntegration(),
      onSuccess: () => {
        InstitutionsManager.institution.updateFromReact({
          lmsConnectionSettings: null,
        });
        setIsDisabling(false);
      },
      onCancel: () => {
        setIsDisabling(false);
      },
      onError: () => {
        setIsDisabling(false);
      },
      successMessage: t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.LMS_DISABLED(),
      title: t.DASHBOARD.CONFIRMATION.ARE_YOU_SURE(),
      bodyText: t.INSTITUTIONS.ADVANCED_SETTINGS.DISABLE_LMS_DESCRIPTION(getProviderName()),
      confirmDataQa: config.pendo.settings.lms.disableConfirm,
      cancelDataQa: config.pendo.settings.lms.disableCancel,
    });
  };

  const onAddNewTopics = (
    {
      topics,
      defaultTopics,
    }: {
      topics: string[];
      defaultTopics: string[],
    },
  ) => {
    setWorkdayConfig({
      ...workdayConfig,
      topics,
      defaultTopics,
    });
    InstitutionsManager.institution.updateFromReact({
      workdayLearnConfig: {
        ...workdayConfig,
        topics,
        defaultTopics,
      },
    });
    setIsTopicsFormDirty(false);
    setShowManageTopicsModal(false);
  };

  const handleCloseManageTopicsModal = useCallback(() => {
    if (isTopicsFormDirty) {
      dispatch(openConfirmationDialog({
        title: t.FORM.UNSAVED_CHANGES.ARE_YOU_SURE(),
        confirmText: t.FORM.CLOSE_ANYWAY(),
        cancelText: t.FORM.DO_NOT_CLOSE(),
        icon: 'warning',
        onConfirm: () => {
          setIsTopicsFormDirty(false);
          setShowManageTopicsModal(false);
        },
      }));
    } else {
      setShowManageTopicsModal(false);
    }
  }, [dispatch, isTopicsFormDirty]);

  const disableWorkday = () => {
    setIsDisabling(true);
    props.onDisable({
      action: () => dispatch(configureWorkday({
        institutionId: InstitutionsManager.institution.id,
        workdayLearnEnabled: false,
      })),
      onSuccess: () => {
        setWorkdayConfig(null);
        setIsWorkdayEnabled(false);
        setLmsConnectionSettings(null);
        setIsDisabling(false);
        InstitutionsManager.institution.updateFromReact({
          lmsConnectionSettings: null,
          isWorkdayLearnEnabled: false,
          workdayLearnConfig: null,
        });
      },
      onCancel: () => {
        setIsDisabling(false);
      },
      onError: () => {
        setIsDisabling(false);
      },
      successMessage: t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.LMS_DISABLED(),
      title: t.DASHBOARD.CONFIRMATION.ARE_YOU_SURE(),
      bodyText: t.INSTITUTIONS.ADVANCED_SETTINGS.DISABLE_LMS_DESCRIPTION(getProviderName()),
      confirmDataQa: config.pendo.settings.lms.disableConfirm,
      cancelDataQa: config.pendo.settings.lms.disableCancel,
    });
  };

  const description = [];
  let lmsDescription: string | string[];
  let ctaText = showConfigureBtn() ? t.INSTITUTIONS.ADVANCED_SETTINGS.CONFIGURE() : null;
  let showSaba = false;

  if (showConfigureBtn()) {
    if (lmsEnabled) {
      description.push(t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.SECTION_DESCRIPTION_ENABLED());
    } else {
      description.push(t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.SECTION_DESCRIPTION());
    }
  }
  if (lmsConnectionSettings?.sabaDomainName) {
    lmsDescription = t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.ENABLED_SECTION.SABA_DESCRIPTION();
    ctaText = t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.DISABLE_CTA();
    showSaba = true;
  }
  if (showSumTotalSection()) {
    lmsDescription = [t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.ENABLED_SECTION.SUMTOTAL_DESCRIPTION()];
    ctaText = t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.DISABLE_CTA_SUM_TOTAL();
  }
  if (showCornerstoneSection()) {
    lmsDescription = [t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.ENABLED_SECTION.CSOD_DESCRIPTION()];
    ctaText = t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.DISABLE_CTA_CSOD();
  }
  if (showlearnUponSection()) {
    lmsDescription = [t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.ENABLED_SECTION.LEARNUPON_DESCRIPTION()];
    ctaText = t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.DISABLE_CTA_LEARNUPON();
  }
  if (showWorkdayLearnSection()) {
    lmsDescription = [t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.ENABLED_SECTION.WORKDAY_LEARN_DESCRIPTION()];
    ctaText = t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.DISABLE_CTA_WORKDAY_LEARN();
  }
  if (isDisabling) {
    ctaText = t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.DISABLING();
  }

  const renderExtras = () => {
    if (showSaba) {
      return (
        <React.Fragment>
          {lmsConnectionSettings?.sabaDomainName && (
            <div className='d-flex flex-column'>
              <div className='text-body mb-2'>
                {lmsDescription}
              </div>
              <div className='text-body'>
                <b>{t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.ENABLED_SECTION.API_ENDPOINT()}</b>
                <span>{lmsConnectionSettings?.clientEndpoint}</span>
              </div>
              <div className='text-body'>
                <b>{t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.ENABLED_SECTION.DOMAIN()}</b>
                <span>{lmsConnectionSettings?.sabaDomainName}</span>
              </div>
              <div className='text-body mb-2'>
                <b>{t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.ENABLED_SECTION.COURSE_NUMBER_CONFIGURATION()}</b>
                {lmsConnectionSettings?.lms === providerTypes.SABA && (
                  <span>
                    {lmsConnectionSettings?.sabaAutoAssignNumbers
                      ? t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.MODAL.SABA.STEP_2.ENABLE_AUTO_ASSIGN()
                      : t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.MODAL.SABA.STEP_2.DISABLE_AUTO_ASSIGN()}
                  </span>
                )}
              </div>
              <Button
                variant='secondary'
                onClick={disableLms}
                className='mt-6 align-self-center'
                data-qa={config.pendo.settings.lms.disable}
              >
                {ctaText}
              </Button>
            </div>
          )}
        </React.Fragment>
      );
    }
    if (showWorkdayLearnSection()) {
      return (
        <div className='d-flex mt-6'>
          <Button
            variant='secondary'
            onClick={disableWorkday}
            data-qa={config.pendo.settings.lms.disable}
            disabled={isDisabling}
          >
            {ctaText}
          </Button>
          <Button
            variant='primary'
            onClick={() => setShowManageTopicsModal(true)}
            className='ml-2'
            data-qa={config.pendo.settings.lms.manageTopics}
          >
            {t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.MANAGE_TOPICS()}
          </Button>
          {showManageTopicsModal && (
            <NvModal
              show
              width={720}
              type={ModalType.DYNAMIC}
              header={t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.MANAGE_TOPICS()}
              onClose={handleCloseManageTopicsModal}
              body={(
                <div className='d-flex flex-column justify-content-center align-items-center px-6 py-4'>
                  <ManageTopics
                    topics={workdayConfig.topics || []}
                    defaultTopics={workdayConfig.defaultTopics || []}
                    onAddTopics={(params) => onAddNewTopics(params)}
                    onDirtyChange={setIsTopicsFormDirty}
                    isEditing
                  />
                </div>
              )}
            />
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <SectionContent
      header={t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.SECTION_TITLE()}
      description={showConfigureBtn() ? description : lmsDescription as []}
      showCTAButton={!!ctaText && !showSaba && !showWorkdayLearnSection()}
      ctaText={ctaText}
      onAction={() => (showConfigureBtn() ? configureLms() : disableLms())}
      disabled={showConfigureBtn() ? !lmsEnabled : isDisabling}
      buttonVariant={showConfigureBtn() ? 'primary' : 'secondary'}
      dataQa={config.pendo.settings.lms.cta}
      extras={renderExtras()}
    />
  );
};

export default LmsIntegration;
